import React, { useEffect, useRef, useState } from 'react';
import taskService, { PickableUser } from '../../services/taskService';
import '../../assets/styles/Workflow.css';

interface UserPickerProps {
  value: number | null;
  onChange: (userId: number | null, user: PickableUser | null) => void;
  role?: string;
  excludeRoles?: string[];
  placeholder?: string;
  /** Pre-fills the display text with an already-known user (e.g. editing a
   * subtask that already has an assignee) instead of starting blank. */
  initialUser?: PickableUser | null;
}

const UserPicker: React.FC<UserPickerProps> = ({ value, onChange, role, excludeRoles, placeholder, initialUser }) => {
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState<PickableUser[]>([]);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<PickableUser | null>(initialUser || null);
  const [highlighted, setHighlighted] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const excludeKey = (excludeRoles || []).join(',');

  useEffect(() => {
    if (!open) return;
    let active = true;
    taskService.listPickableUsers(role, query, excludeRoles).then((users) => {
      if (active) { setOptions(users); setHighlighted(0); }
    }).catch(() => { if (active) setOptions([]); });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, role, excludeKey, open]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!value) setSelected(null);
  }, [value]);

  // initialUser often arrives asynchronously (the caller looks the id up by
  // a separate API call) - it can still be null on this component's own
  // first mount, so the useState initializer above alone isn't enough. Sync
  // it in whenever it changes, but only when there's nothing already
  // selected (or the selection is now stale) so this never overwrites a
  // choice the person just made in this picker.
  useEffect(() => {
    if (initialUser && (!selected || selected.id !== initialUser.id)) {
      setSelected(initialUser);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialUser]);

  const pick = (user: PickableUser) => {
    setSelected(user);
    setQuery('');
    setOpen(false);
    onChange(user.id, user);
  };

  return (
    <div className="wf-picker" ref={containerRef}>
      <input
        type="text"
        className="wf-picker-input"
        placeholder={placeholder || 'Type a name to search...'}
        value={open ? query : (selected ? `${selected.name} (${selected.role_name})` : '')}
        onFocus={() => setOpen(true)}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        onKeyDown={(e) => {
          if (!open || options.length === 0) return;
          if (e.key === 'ArrowDown') { e.preventDefault(); setHighlighted((i) => (i + 1) % options.length); }
          else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlighted((i) => (i - 1 + options.length) % options.length); }
          else if (e.key === 'Enter') { e.preventDefault(); pick(options[highlighted]); }
          else if (e.key === 'Escape') { setOpen(false); }
        }}
      />
      {open && (
        <div className="wf-picker-dropdown">
          {options.length === 0 ? (
            <div className="wf-picker-empty">No matches</div>
          ) : (
            options.map((u, idx) => (
              <div
                key={u.id}
                className={`wf-picker-option${idx === highlighted ? ' active' : ''}`}
                onMouseEnter={() => setHighlighted(idx)}
                onClick={() => pick(u)}
              >
                <span>{u.name} <span style={{ color: '#9ca3af' }}>({u.email})</span></span>
                <span className="wf-role-badge">{u.role_name}</span>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default UserPicker;
